import { Box, Typography } from '@material-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useObjectivesList } from '../../../../hooks/useObjectivesList';
import { ObjectiveSelect } from '../../../../components/ObjectiveSelect';
import { useContentContext } from '../../context/content-context';
import {
  DYNAMIC_VIEW_REDUCER_ACTIONS,
  useDynamicViewState,
  useDynamicViewDispatch,
} from '../../context/dynamic-view-context';
import { getObjetivoDefaultStep } from '../../utils/default-step';

/**
 * @fileoverview Componente de filtros para las tarjetas del Panel Dinámico.
 * @module DpCardFilters
 */

/**
 * Componente DpCardFilters.
 *
 * Muestra y gestiona la configuración específica de una tarjeta (widget).
 *
 * Funcionalidad:
 * - En modo edición: Muestra un selector para cambiar el objetivo asociado a la tarjeta.
 * - En modo visualización: Muestra el nombre del objetivo seleccionado actualmente.
 * - Actualiza el layout global al cambiar el objetivo, recalculando el paso por defecto.
 *
 * @returns {React.ReactElement} Contenedor de filtros de la tarjeta.
 *
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export const DpCardFilters = React.memo(function DpCardFilters() {
  const { t } = useTranslation();
  const {
    data: objectives,
    isLoading: isLoadingObjectiveList,
  } = useObjectivesList({ fromStorage: true });
  const { slot } = useContentContext();
  const state = useDynamicViewState();
  const dispatch = useDynamicViewDispatch();

  function setObjetivoId(objetivoId) {
    const pasoId = getObjetivoDefaultStep({ objetivoId });
    const updatedLayout = state.activeLayout.map((item) => {
      if (item.id === slot.id) {
        return { ...item, objetivoId, customProperties: { pasoId } };
      }
      return item;
    });
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.UPDATE_TEMPLATE,
      payload: updatedLayout,
    });
  }

  const selectedObjective = React.useMemo(() => {
    const cardObjective = objectives?.find(
      (obj) => obj.objetivo_id === (slot.objetivoId ?? ''),
    );
    return cardObjective;
  }, [objectives, slot.objetivoId]);

  return (
    <Box padding={0} display="flex">
      {state.isEditing ? (
        <Box width={300}>
          <ObjectiveSelect
            objetivoId={slot.objetivoId ?? ''}
            objetivos={objectives}
            isLoading={isLoadingObjectiveList}
            setObjetivoId={setObjetivoId}
          />
        </Box>
      ) : (
        <Typography style={{ marginTop: 5, color: '#6B7280', fontSize: '0.875rem', fontWeight: 500 }}>
          {t('dpCardFilters.objective')} {selectedObjective?.nombre ?? ''}
        </Typography>
      )}
    </Box>
  );
});
